import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../utils/LanguageContext';
import axios from 'axios';
import { Loader, Calendar, Folder, ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import getHelmetConfig from "../utils/helmetConfig";

const API_BASE_URL = 'https://texla.uz/api/v1';

const SingleNewsPage = () => {
    const { id } = useParams();
    const { t, language } = useLanguage();
    const [news, setNews] = useState(null);
    const [relatedNews, setRelatedNews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchNews();
        window.scrollTo(0, 0);
    }, [id, language]);

    const fetchNews = async () => {
        setLoading(true);
        try {
            // Fetch current news
            const response = await axios.get(`${API_BASE_URL}/news/${id}/`);
            if (response.data) {
                const newsData = response.data;
                const dateObj = new Date(newsData.created_at);
                const day = String(dateObj.getDate()).padStart(2, '0');
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const year = dateObj.getFullYear();
                const formattedDate = `${day}.${month}.${year}`;

                setNews({
                    id: newsData.id,
                    title: newsData[`title_${language}`] || newsData.title_uz || newsData.title,
                    text: newsData[`text_${language}`] || newsData.text_uz || newsData.text,
                    image: newsData.image || 'https://via.placeholder.com/800x400',
                    category: newsData.categories?.[0]?.[`title_${language}`] || newsData.categories?.[0]?.title_uz || newsData.categories?.[0]?.title || t('home.news'),
                    date: formattedDate,
                    created_at: newsData.created_at
                });
            }

            // Fetch all news for related items
            const allNewsResponse = await axios.get(`${API_BASE_URL}/news/`);
            if (allNewsResponse.data && Array.isArray(allNewsResponse.data)) {
                const related = allNewsResponse.data
                    .filter(item => item.id !== parseInt(id))
                    .slice(0, 3)
                    .map(item => {
                        const dateObj = new Date(item.created_at);
                        const day = String(dateObj.getDate()).padStart(2, '0');
                        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                        const year = dateObj.getFullYear();
                        const formattedDate = `${day}.${month}.${year}`;

                        return {
                            id: item.id,
                            title: item[`title_${language}`] || item.title_uz || item.title,
                            image: item.image || 'https://via.placeholder.com/300x200',
                            date: formattedDate,
                        };
                    });
                setRelatedNews(related);
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    // Helper function - HTML tags o'chirish
    const stripHtml = (html) => {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '');
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <Loader className="animate-spin text-gray-400" size={48} />
            </div>
        );
    }

    if (!news) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <Helmet>
                    <title>Yangilik Topilmadi | TexLab</title>
                    <meta name="description" content="Kechirasiz, so'ralgan yangilik topilmadi." />
                </Helmet>
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('shop.notFound')}</h2>
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition"
                    >
                        <ArrowLeft size={20} />
                        {t('news.backToNews')}
                    </Link>
                </div>
            </div>
        );
    }

    const helmetConfig = getHelmetConfig(language);

    return (
        <div className="bg-white">
            <Helmet>
                {news && (
                    <>
                        <title>{news.title} | TexLab Yangiliklar</title>
                        <meta name="description" content={stripHtml(news.text).substring(0, 160)} />
                        <meta name="keywords" content={`TexLab, yangiliklar, ${news.category}, laboratoriya`} />
                        <meta property="og:title" content={news.title} />
                        <meta property="og:description" content={stripHtml(news.text).substring(0, 160)} />
                        <meta property="og:image" content={news.image} />
                    </>
                )}
            </Helmet>

            {/* Breadcrumb */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
                    <Link to={"/"} className="hover:text-gray-900">{t('breadcrumb.home')}</Link>
                    <span>/</span>
                    <Link to={"/blog"} className="hover:text-gray-900">{t('home.news')}</Link>
                    <span>/</span>
                    <span className="text-gray-900 line-clamp-1">{news.title}</span>
                </div>
            </div>

            {/* Main Content */}
            <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Featured Image */}
                <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
                    <img
                        src={news.image}
                        alt={news.title}
                        className="w-full h-96 object-cover"
                    />
                </div>

                {/* Meta Information */}
                <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                        <Calendar size={18} />
                        {news.date}
                    </div>
                    <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
                        <Folder size={18} />
                        {news.category}
                    </div>
                </div>

                {/* Title */}
                <h1 className="text-4xl font-bold text-gray-900 mb-6">{news.title}</h1>

                {/* Content */}
                <div className="prose prose-lg max-w-none text-gray-600 mb-12">
                    <div dangerouslySetInnerHTML={{ __html: news.text }} />
                </div>

                {/* Back Button */}
                <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 text-orange-600 font-semibold hover:text-blue-700 mb-12"
                >
                    <ArrowLeft size={20} />
                    {t('news.backToNews')}
                </Link>
            </article>

            {/* Related News */}
            {relatedNews.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('news.relatedNews')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {relatedNews.map((item) => (
                            <Link
                                key={item.id}
                                to={`/blog/${item.id}`}
                                className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition group"
                            >
                                <div className="h-40 bg-gray-200 overflow-hidden">
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                                    />
                                </div>
                                <div className="p-4">
                                    <div className="text-sm text-gray-500 mb-2 flex items-center gap-1">
                                        <Calendar size={14} />
                                        {item.date}
                                    </div>
                                    <h3 className="font-bold text-gray-900 line-clamp-2 group-hover:text-orange-600 transition">
                                        {item.title}
                                    </h3>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default SingleNewsPage;